import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Box,
	Chip,
	IconButton,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useGetMajorDetailListQuery,
	useGetUserDetailQuery,
	useUnlockDayMutation,
	useUnlockMajorDetailMutation,
} from '@/api/user/userApiSlice';
import {
	User21Filter,
	UserAdvanceSeriesSinglePageResDTO,
} from '@/api/DTO/user/userList.interface';
import { showLabel } from '@/utils/helper';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import qs from 'qs';
import { useGetMajorDetailQuery } from '@/api/exercise/majorApiSlice';

export interface DataGrid {
	updatedAt: string;
}

export default function UnlockSeries21SummaryPage() {
	const { t } = useTranslation();
	const { id, seriesId } = useParams();
	const showModal = useModal();
	const [unlockMajorDetail] = useUnlockMajorDetailMutation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);

	const lockList = [
		{ id: 0, name: t('user.unlock') },
		{ id: 1, name: t('user.finish') },
		{ id: 2, name: t('user.lock') },
	];

	const { data: userDetail }: any = useGetUserDetailQuery(id || '', {
		refetchOnMountOrArgChange: true,
	});

	const { data: details } = useGetMajorDetailQuery(seriesId || '', {
		refetchOnMountOrArgChange: true,
	});

	const defaultPropertyFilter: User21Filter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		id: id || '',
		seriesId: seriesId || '',
	};
	const defaultValues: DefaultValues<User21Filter> = defaultPropertyFilter;
	const methods = useForm<User21Filter>({
		defaultValues,
	});
	const transformedSearchParams: User21Filter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'id',
			'seriesId',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetMajorDetailListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback(
		(response?: UserAdvanceSeriesSinglePageResDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as User21Filter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);
	const handleUnlock = (row: UserAdvanceSeriesSinglePageResDTO) => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('user.unlockExercise')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('user.isUnlockExercises')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await unlockMajorDetail({
						id: id || '',
						seriesId: seriesId || '',
						singleId: row.singleId || '',
					});
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.unlockExerciseSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};

	const columns: GridColDef[] = [
		{
			field: 'serialNumber',
			headerName: t('marjorExercise.index'),
			flex: 21,
		},
		{
			field: 'titleZh',
			headerName: t('day21.exerciseTitleZh'),
			flex: 21,
		},
		{
			field: 'published',
			headerName: t('user.exerciseOnStatus'),
			flex: 21,
			renderCell: (params) => (
				<Chip
					color={params.row.published ? 'chipPrimary' : 'default'}
					label={params.row.published ? t('day21.on') : t('day21.off')}
				/>
			),
		},
		{
			field: 'userExerciseStatus',
			headerName: t('user.userExerciseStatus'),
			flex: 21,
			renderCell: (params) =>
				typeof params.row.userExerciseStatus == 'number' ? (
					<Chip
						color={
							params.row.userExerciseStatus == 1
								? 'chipPrimary'
								: params.row.userExerciseStatus == 2
								? 'default'
								: 'chipTertiary'
						}
						label={showLabel(params.row.userExerciseStatus, lockList)}
					/>
				) : (
					'-'
				),
		},
		{
			field: 'updatedAt',
			headerName: t('user.finalCompletionTime'),
			flex: 21,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) =>
				params.row.userExerciseStatus == 2 && (
					<IconButton color='primary'>
						<LockOpenIcon onClick={() => handleUnlock(params.row)} />
					</IconButton>
				),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{details?.data.titleZh}({userDetail?.data.username},
					{userDetail?.data.nickname})
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<Typography variant='h6'>{t('day21.exerciseList')}</Typography>
					<FormProvider {...methods}>
						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.singleId}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
